import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import apiClient from "../utilities/apiClient";
import { normalizePrinterSettings } from "../utilities/salesOrderReceiptUtils";
import {
  isBusinessSettingsConfigured,
  isHomSetupComplete,
  isPrinterSettingsConfigured,
} from "../utilities/homSettingsValidation";

let homSettingsLoadedForBasePath = null;
let homSettingsLoadPromise = null;

async function fetchHomSettings(dispatch, basePath) {
  if (homSettingsLoadedForBasePath === basePath) return;
  if (homSettingsLoadPromise) {
    await homSettingsLoadPromise;
    return;
  }

  homSettingsLoadPromise = Promise.all([
    apiClient
      .get("/companyInfo/receiptMetaDataPrinter", { context: "Loading Printer Settings", silent: true })
      .then((data) => {
        dispatch({ type: "PRINTER_SETTINGS", printer_settings: normalizePrinterSettings(data) });
      })
      .catch(() => null),
    apiClient
      .get("/companyInfo/receiptMetaData", { context: "Loading Business Info", silent: true })
      .then((data) => {
        dispatch({ type: "RECEIPT_METADATA", receipt_metadata: data });
        dispatch({ type: "COMPANY_INFO", company_info: data });
      })
      .catch(() => null),
  ]).finally(() => {
    homSettingsLoadedForBasePath = basePath;
    homSettingsLoadPromise = null;
  });

  await homSettingsLoadPromise;
}

export function useHomSettingsData() {
  const dispatch = useDispatch();
  const { basePath, appType } = useSelector((state) => state.globalState);
  const [isLoading, setIsLoading] = useState(false);

  const isHomsApp = appType?.startsWith("HOMS");

  useEffect(() => {
    if (!basePath || !isHomsApp) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    fetchHomSettings(dispatch, basePath).finally(() => {
      if (!cancelled) setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [dispatch, basePath, isHomsApp]);

  useEffect(() => {
    if (!basePath) {
      homSettingsLoadedForBasePath = null;
      homSettingsLoadPromise = null;
      return;
    }
    if (homSettingsLoadedForBasePath && homSettingsLoadedForBasePath !== basePath) {
      homSettingsLoadedForBasePath = null;
    }
  }, [basePath]);

  return { isLoading };
}

export function useHomSetupStatus() {
  const { appType } = useSelector((state) => state.globalState);
  const company_info = useSelector((state) => state.companyInfoState);
  const printer_Setting = useSelector((state) => state.printerSettingState);
  const { isLoading } = useHomSettingsData();

  const isHomsApp = appType?.startsWith("HOMS");
  const printerSettings = printer_Setting?.printer_settings;
  const receiptMetadata = company_info?.receipt_metadata;

  const printerComplete = isPrinterSettingsConfigured(printerSettings);
  const businessComplete = isBusinessSettingsConfigured(receiptMetadata);
  const setupComplete = isHomSetupComplete(printerSettings, receiptMetadata);

  return {
    isHomsApp,
    isLoading: isHomsApp && isLoading,
    printerComplete,
    businessComplete,
    setupComplete,
    printerSettings,
    receiptMetadata,
  };
}
